import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getCurrentPeriodStats,
  getNotifications,
  getRemainingTo50h,
  getRemainingTo100h,
} from '../../api';
import {
  NotificationItem,
  PilotPercent,
  RemainingTO,
} from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { useAircraft, aircraftLabel } from '../../aircraft/AircraftContext';
import { useFeedback } from '../../hooks/useFeedback';
import { getApiErrorMessage } from '../../utils/apiError';
import { DateFormat } from '../../utils/DateFormat';
import {
  calendarOverdue,
  formatDaysPart,
  formatHoursPart,
  hoursOverdue,
  hoursUnit,
  remainingOverdue,
} from '../../utils/toRemaining';
import PilotBadge from '../../components/PilotBadge/PilotBadge';
import { formatPercent } from '../../utils/formatMetric';
import './HomePage.css';

const HomePage = () => {
  const {
    isAdmin,
    canFlyOrBook,
    isTechnician,
    isGuest,
    user,
    clubs,
    switchClub,
  } = useAuth();
  const {
    aircraftId,
    aircraftList,
    current,
    dataRevision,
    setAircraftId,
  } = useAircraft();
  const { showError, feedbackNode } = useFeedback();
  const dateFormat = user?.date_format === 'long' ? 'long' : 'dot';
  const [to50, setTo50] = useState<RemainingTO | null>(null);
  const [to100, setTo100] = useState<RemainingTO | null>(null);
  const [pilots, setPilots] = useState<PilotPercent[]>([]);
  const [totalHours, setTotalHours] = useState<number | null>(null);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [adminSummary, setAdminSummary] = useState<NotificationItem[]>([]);
  const [clubSwitching, setClubSwitching] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [to50Res, to100Res, periodRes, notesRes] = await Promise.all([
          getRemainingTo50h(),
          getRemainingTo100h(),
          getCurrentPeriodStats(),
          getNotifications(),
        ]);
        setTo50(to50Res.data);
        setTo100(to100Res.data);
        setPilots(periodRes.data.pilots || []);
        setTotalHours(periodRes.data.total_hours ?? null);
        setNotifications(notesRes.data.items || []);
        setAdminSummary([
          ...(notesRes.data.admin_vlek_summary || []),
          ...(notesRes.data.admin_slg_summary || []),
          ...(notesRes.data.admin_insurance_summary || []),
        ]);
      } catch (error) {
        showError(getApiErrorMessage(error, 'Ошибка загрузки главной'));
      }
    };
    load();
  }, [aircraftId, dataRevision, showError]);

  const nearestTo = useMemo(() => {
    const active = [to50, to100].find((item) => item?.active);
    if (active) return active;
    return to50?.next_required === 'ТО-50' ? to50 : to100 || to50;
  }, [to50, to100]);

  const urgentAdmin = useMemo(
    () =>
      adminSummary.filter(
        (item) => item.status === 'overdue' || item.status === 'soon'
      ),
    [adminSummary]
  );

  const notificationRows = isAdmin
    ? notifications.length > 0
      ? notifications
      : urgentAdmin.map((item) => {
          const kind =
            item.type === 'slg'
              ? 'СЛГ'
              : item.type === 'insurance'
                ? 'Страховка'
                : 'ВЛЭК';
          const neuter = item.type === 'slg';
          const feminine = item.type === 'insurance';
          const overdueWord = feminine
            ? 'просрочена'
            : neuter
              ? 'просрочено'
              : 'просрочен';
          return {
            ...item,
            message: item.overdue
              ? `${kind} у ${item.user_name} ${overdueWord}`
              : `${kind} у ${item.user_name} истекает через ${item.days_left} дн.`,
          };
        })
    : notifications;

  const hoursOver = hoursOverdue(nearestTo);
  const daysOver = calendarOverdue(nearestTo);
  const toOverdue = remainingOverdue(nearestTo);
  const hoursLabel = formatHoursPart(nearestTo);
  const daysLabel = formatDaysPart(nearestTo);
  const toLabel = hoursLabel ?? '—';

  const aircraftHint = current
    ? aircraftLabel(current)
    : aircraftId
      ? `Борт #${aircraftId}`
      : '';
  const activeClubId =
    user?.club_id ?? clubs.find((c) => c.active)?.club_id ?? 0;
  const clubHint =
    user?.club_name ||
    clubs.find((c) => c.club_id === activeClubId)?.club_name ||
    '';

  const handleAircraftChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    if (!Number.isFinite(id)) return;
    setAircraftId(id);
  };

  const handleClubChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    if (!Number.isFinite(id) || id === activeClubId) return;
    setClubSwitching(true);
    try {
      await switchClub(id);
    } catch (error) {
      showError(getApiErrorMessage(error, 'Не удалось сменить клуб'));
    } finally {
      setClubSwitching(false);
    }
  };

  return (
    <div className="home-page">
      {feedbackNode}

      <section className="home-hero">
        <div className="home-hero-copy">
          <h1 className="home-brand">Бортовой Журнал</h1>
          <div className="home-context">
            {clubs.length > 1 ? (
              <select
                id="home-club-select"
                name="home_club_id"
                className="home-context-select"
                value={activeClubId || ''}
                disabled={clubSwitching}
                onChange={(e) => void handleClubChange(e)}
                aria-label="Выбор клуба"
              >
                {clubs.map((item) => (
                  <option key={item.club_id} value={item.club_id}>
                    {item.club_name}
                  </option>
                ))}
              </select>
            ) : clubHint ? (
              <p className="home-context-label">{clubHint}</p>
            ) : null}
            {aircraftList.length > 1 ? (
              <select
                id="home-aircraft-select"
                name="home_aircraft_id"
                className="home-context-select"
                value={aircraftId}
                onChange={handleAircraftChange}
                aria-label="Выбор самолёта"
              >
                {aircraftList.map((item) => (
                  <option key={item.id} value={item.id}>
                    {aircraftLabel(item)}
                  </option>
                ))}
              </select>
            ) : aircraftHint ? (
              <p className="home-context-label">{aircraftHint}</p>
            ) : null}
          </div>
        </div>

        <Link to="/maintenance" className="home-to-focus home-to-merged">
          <span className="home-to-label">
            До {nearestTo?.title || 'ТО'}
          </span>
          <span
            className={`home-to-number${
              nearestTo?.status === 'ok' && !toOverdue ? ' ok' : ' warn'
            }${daysLabel ? ' split' : ''}`}
          >
            {toLabel}
            {hoursLabel != null ? (
              <span className="home-to-unit">{hoursUnit(nearestTo)}</span>
            ) : null}
            {daysLabel != null ? (
              <>
                <span className="home-to-slash">/</span>
                {daysLabel}
                <span className="home-to-unit">дн</span>
              </>
            ) : null}
          </span>

          {hoursOver && nearestTo?.hard_remaining != null ? (
            <span className="home-to-ban">
              {Number(nearestTo.hard_remaining) < 0
                ? 'Полёты запрещены'
                : `Запрет через ${nearestTo.hard_remaining} м/ч`}
            </span>
          ) : daysOver ? (
            <span className="home-to-ban">Прошёл год с последней ТО-100</span>
          ) : (
            <span className="home-to-hint">Открыть техническое обслуживание</span>
          )}

          <div className="home-to-percent">
            <div className="home-to-percent-head">
              <h2>Текущая процентовка</h2>
              {totalHours != null ? (
                <span className="home-board-meta">{totalHours} м/ч с ТО</span>
              ) : null}
            </div>

            {pilots.length === 0 ? (
              <p className="home-to-percent-empty">
                Пока нет налёта за период
              </p>
            ) : (
              <ul className="home-percent-list">
                {pilots.map((p) => {
                  const width = Math.max(0, Math.min(100, Number(p.percent) || 0));
                  const barColor =
                    p.color && /^#[0-9A-Fa-f]{6}$/i.test(p.color)
                      ? p.color
                      : 'rgba(125, 255, 239, 0.35)';
                  return (
                    <li key={p.pilot}>
                      <div className="home-percent-track">
                        <span
                          className="home-percent-fill"
                          style={{ width: `${width}%`, backgroundColor: barColor }}
                        />
                        <PilotBadge
                          name={p.pilot}
                          color={p.color}
                          className="home-percent-label"
                        />
                      </div>
                      <span className="home-percent-value">
                        {formatPercent(p.percent)}%
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Link>

        <div className="home-cta home-cta-vertical">
          {canFlyOrBook ? (
            <>
              <Link to="/flights" className="home-cta-btn home-cta-primary">
                Добавить полёт
              </Link>
              <Link to="/bookings" className="home-cta-btn">
                Добавить бронь
              </Link>
            </>
          ) : null}

          {isTechnician ? (
            <>
              <Link to="/issues" className="home-cta-btn home-cta-primary">
                Неисправности
              </Link>
              <Link to="/maintenance" className="home-cta-btn">
                Провести ТО
              </Link>
            </>
          ) : null}

          {isGuest ? (
            <>
              <Link to="/stats" className="home-cta-btn home-cta-primary">
                История полётов
              </Link>
              <Link to="/aircraft" className="home-cta-btn">
                Самолёт
              </Link>
            </>
          ) : null}
        </div>
      </section>

      {!isGuest ? (
        <section className="home-board" aria-label="Уведомления">
          <Link
            to="/profile?settings=notifications"
            className="home-board-block home-board-block-single"
          >
            <div className="home-board-head">
              <h2>Уведомления</h2>
              <span className="home-board-meta">Все</span>
            </div>
            {notificationRows.length === 0 ? (
              <p className="home-board-empty">Срочных уведомлений нет</p>
            ) : (
              <ul className="home-notifications-list">
                {notificationRows.slice(0, 4).map((item) => (
                  <li
                    key={`${item.type}-${item.user_id ?? item.aircraft_id}-${item.next_date || 'x'}`}
                    className={
                      item.overdue || item.severity === 'critical'
                        ? 'critical'
                        : 'warning'
                    }
                  >
                    <strong>{item.user_name}</strong>
                    <span>
                      {item.message ||
                        (item.type === 'birthday'
                          ? `Сегодня день рождения у ${item.user_name}`
                          : item.type === 'slg'
                            ? item.overdue
                              ? 'СЛГ просрочено'
                              : `СЛГ через ${item.days_left} дн.`
                            : item.type === 'insurance'
                              ? item.overdue
                                ? 'Страховка просрочена'
                                : `Страховка через ${item.days_left} дн.`
                              : item.overdue
                                ? 'ВЛЭК просрочен'
                                : `ВЛЭК через ${item.days_left} дн.`)}
                    </span>
                    {item.next_date ? (
                      <span className="home-notification-date">
                        {DateFormat(item.next_date, dateFormat)}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </Link>
        </section>
      ) : (
        <section className="home-board" aria-label="Неисправности">
          <Link to="/issues" className="home-board-block home-board-block-single">
            <div className="home-board-head">
              <h2>Неисправности</h2>
            </div>
            <p className="home-board-empty">
              Состояние борта — только просмотр
            </p>
          </Link>
        </section>
      )}

      <nav className="home-quick" aria-label="Разделы">
        <Link to="/aircraft" className="home-quick-link">
          <img src="/plane.png" alt="Самолёт" className="home-quick-icon" />
          Самолёт
        </Link>
        {!isGuest ? (
          <Link to="/profile" className="home-quick-link">
            <img src="/me.png" alt="Личный кабинет" className="home-quick-icon" />
            Личный кабинет
          </Link>
        ) : null}
        <Link to="/club" className="home-quick-link">
          <img src="/star.png" alt="Клуб" className="home-quick-icon" />
          Клуб
        </Link>
        {!isGuest ? (
          <Link
            to="/profile?settings=general"
            className="home-quick-link"
          >
            <img
              src="/settings.png"
              alt="Настройки"
              className="home-quick-icon"
            />
            Настройки
          </Link>
        ) : null}
      </nav>
    </div>
  );
};

export default HomePage;
